import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { eventsApi } from '../services/api';
import { formatShiftTime } from '../lib/shiftTime';
import { ArrowLeft, Printer } from 'lucide-react';

// ===========================================
// BEO / CLIENT PROPOSAL — printable document
// ===========================================
// One route, two renderings:
//   /events/:id/beo              → internal BEO (everything: staffing, prep,
//                                  briefing notes, cost estimates)
//   /events/:id/beo?view=client  → client proposal (no internal info)
// Printing uses the browser's print-to-PDF via window.print(), same as the
// deliveries/labels printing on the Production page. The toolbar is hidden
// with print CSS so the printed page is just the document.

const money = (cents: number) => `$${(cents / 100).toFixed(2)}`;

export default function EventBeoPage() {
  const { id = '' } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const isClientView = searchParams.get('view') === 'client';

  const { data: event, isLoading } = useQuery({
    queryKey: ['event-detail', id],
    queryFn: async () => {
      const response = await eventsApi.getById(id);
      return response.data?.data;
    },
    enabled: !!id,
  });

  if (isLoading || !event) {
    return <div className="p-6 text-center text-slate-500">Preparing the document…</div>;
  }

  const totals = event.totals || { lines: [], menuSubtotalCents: 0, roomFeeCents: 0, serviceCents: 0, taxCents: 0, totalCents: 0, depositCents: 0, servicePct: 0, taxPct: 0 };
  const estimates = event.estimates;
  const terms = event.eventSettings?.terms || '';
  const roleRequirements: Array<{ role: string; minCount: number }> = event.roleRequirements || [];
  const assignments: any[] = (event.assignments || []).filter((a: any) => a.status !== 'cancelled');

  const dateLabel = new Date(`${event.date}T00:00:00`).toLocaleDateString([], {
    weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
  });
  const timeLabel = `${formatShiftTime(event.startTime)} – ${formatShiftTime(event.endTime)}`;

  const assignedByRole = new Map<string, string[]>();
  for (const a of assignments) {
    const name = a.employee ? `${a.employee.firstName} ${a.employee.lastName}` : 'Unassigned';
    assignedByRole.set(a.role, [...(assignedByRole.get(a.role) || []), name]);
  }

  return (
    <div className="min-h-screen bg-slate-100 print:bg-white">
      <style>{`
        @media print {
          .no-print { display: none !important; }
          .beo-sheet { box-shadow: none !important; margin: 0 !important; border-radius: 0 !important; max-width: none !important; }
          @page { margin: 14mm; }
        }
      `}</style>

      {/* Toolbar (screen only) */}
      <div className="no-print sticky top-0 z-10 bg-white border-b border-slate-200 px-4 py-3 flex items-center gap-3">
        <button
          onClick={() => navigate(`/events/${id}`)}
          className="p-2 hover:bg-slate-100 rounded-lg"
          title="Back to the event"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <p className="font-semibold text-slate-900">{isClientView ? 'Client proposal' : 'Banquet Event Order (internal)'}</p>
          <p className="text-xs text-slate-500">{event.name} · {dateLabel}</p>
        </div>
        <div className="flex rounded-lg border border-slate-200 overflow-hidden text-sm">
          <button
            onClick={() => setSearchParams({})}
            className={!isClientView ? 'px-3 py-1.5 bg-primary-600 text-white' : 'px-3 py-1.5 bg-white text-slate-600 hover:bg-slate-50'}
          >
            Internal BEO
          </button>
          <button
            onClick={() => setSearchParams({ view: 'client' })}
            className={isClientView ? 'px-3 py-1.5 bg-primary-600 text-white' : 'px-3 py-1.5 bg-white text-slate-600 hover:bg-slate-50'}
          >
            Client proposal
          </button>
        </div>
        <button onClick={() => window.print()} className="btn btn-primary inline-flex items-center gap-2">
          <Printer className="w-4 h-4" />
          Print / PDF
        </button>
      </div>

      {/* Document */}
      <div className="beo-sheet bg-white max-w-3xl mx-auto my-6 shadow-lg rounded-xl p-8 md:p-10 text-slate-900">
        {/* Header */}
        <div className="flex items-start justify-between pb-4 border-b-2 border-slate-900">
          <div>
            <p className="text-xs uppercase tracking-widest text-slate-500">
              {isClientView ? 'Event proposal' : 'Banquet event order'}
            </p>
            <h1 className="text-2xl font-bold mt-1">{event.name}</h1>
            <p className="text-slate-600 mt-0.5">{event.restaurantName}</p>
          </div>
          <div className="text-right text-sm text-slate-600">
            <p className="font-semibold text-slate-900">{dateLabel}</p>
            <p>{timeLabel}</p>
            {event.spaceName && <p>{event.spaceName}</p>}
            {!!event.expectedGuests && <p>{event.expectedGuests} guests</p>}
          </div>
        </div>

        {/* Client block */}
        <div className="grid grid-cols-2 gap-6 py-4 border-b border-slate-200 text-sm">
          <div>
            <p className="text-xs uppercase tracking-wide text-slate-400 mb-1">
              {isClientView ? 'Prepared for' : 'Client'}
            </p>
            <p className="font-medium">{event.clientName || '—'}</p>
            {event.clientCompany && <p className="text-slate-600">{event.clientCompany}</p>}
            {event.clientEmail && <p className="text-slate-600">{event.clientEmail}</p>}
            {event.clientPhone && <p className="text-slate-600">{event.clientPhone}</p>}
          </div>
          <div>
            <p className="text-xs uppercase tracking-wide text-slate-400 mb-1">Event</p>
            <p className="text-slate-600 capitalize">{(event.eventType || 'private party').replace(/_/g, ' ')}</p>
            {event.proposalSentAt && (
              <p className="text-slate-600">Proposal sent {new Date(event.proposalSentAt).toLocaleDateString()}</p>
            )}
            {event.depositPaidAt && <p className="text-slate-600">Deposit received</p>}
          </div>
        </div>

        {/* Menu */}
        <div className="py-4 border-b border-slate-200">
          <p className="text-xs uppercase tracking-wide text-slate-400 mb-2">Menu</p>
          {totals.lines.length === 0 ? (
            <p className="text-sm text-slate-500">Menu to be finalized.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-slate-500 border-b border-slate-200">
                  <th className="py-1.5 pr-2 font-medium">Item</th>
                  <th className="py-1.5 pr-2 font-medium text-right">Quantity</th>
                  <th className="py-1.5 pr-2 font-medium text-right">Price</th>
                  <th className="py-1.5 font-medium text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                {totals.lines.map((line: any, idx: number) => (
                  <tr key={idx} className="border-b border-slate-100">
                    <td className="py-2 pr-2">
                      {line.name}
                      {line.notes && <span className="block text-xs text-slate-500">{line.notes}</span>}
                    </td>
                    <td className="py-2 pr-2 text-right">
                      {line.totalQty}
                      {line.qtyMode === 'per_guest' && (
                        <span className="text-xs text-slate-400"> ({line.qty}/guest)</span>
                      )}
                    </td>
                    <td className="py-2 pr-2 text-right">{money(line.unitPriceCents)}</td>
                    <td className="py-2 text-right">{money(line.totalCents)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Staffing plan (internal only) */}
        {!isClientView && (
          <div className="py-4 border-b border-slate-200">
            <p className="text-xs uppercase tracking-wide text-slate-400 mb-2">Staffing plan</p>
            {roleRequirements.length === 0 ? (
              <p className="text-sm text-slate-500">No staffing plan yet.</p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-slate-500 border-b border-slate-200">
                    <th className="py-1.5 pr-2 font-medium">Role</th>
                    <th className="py-1.5 pr-2 font-medium text-right">Needed</th>
                    <th className="py-1.5 font-medium">Assigned</th>
                  </tr>
                </thead>
                <tbody>
                  {roleRequirements.map((req) => (
                    <tr key={req.role} className="border-b border-slate-100">
                      <td className="py-2 pr-2 capitalize">{req.role}</td>
                      <td className="py-2 pr-2 text-right">{req.minCount}</td>
                      <td className="py-2">
                        {(assignedByRole.get(req.role) || []).join(', ') || (
                          <span className="text-slate-400">Open</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* Timeline / briefing (internal only) */}
        {!isClientView && (event.briefingNotes || event.notes) && (
          <div className="py-4 border-b border-slate-200">
            <p className="text-xs uppercase tracking-wide text-slate-400 mb-2">Timeline &amp; briefing</p>
            {event.briefingNotes && <p className="text-sm whitespace-pre-line">{event.briefingNotes}</p>}
            {event.notes && <p className="text-sm whitespace-pre-line text-slate-600 mt-2">{event.notes}</p>}
          </div>
        )}

        {/* Totals */}
        <div className="py-4 border-b border-slate-200">
          <p className="text-xs uppercase tracking-wide text-slate-400 mb-2">Summary</p>
          <div className="text-sm space-y-1 max-w-xs ml-auto">
            <div className="flex justify-between text-slate-600"><span>Menu subtotal</span><span>{money(totals.menuSubtotalCents)}</span></div>
            {totals.roomFeeCents > 0 && (
              <div className="flex justify-between text-slate-600"><span>Room fee</span><span>{money(totals.roomFeeCents)}</span></div>
            )}
            {totals.serviceCents > 0 && (
              <div className="flex justify-between text-slate-600"><span>Service ({totals.servicePct}%)</span><span>{money(totals.serviceCents)}</span></div>
            )}
            {totals.taxCents > 0 && (
              <div className="flex justify-between text-slate-600"><span>Tax ({totals.taxPct}%)</span><span>{money(totals.taxCents)}</span></div>
            )}
            <div className="flex justify-between font-bold text-base border-t border-slate-300 pt-1.5 mt-1.5">
              <span>Total</span><span>{money(totals.totalCents)}</span>
            </div>
            {totals.depositCents > 0 && (
              <div className="flex justify-between text-slate-700">
                <span>Deposit due</span>
                <span>
                  {money(totals.depositCents)}
                  {event.depositPaidAt ? ' · received' : ''}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Cost estimates (internal only) */}
        {!isClientView && estimates && (
          <div className="py-4 border-b border-slate-200">
            <p className="text-xs uppercase tracking-wide text-slate-400 mb-2">Internal estimates</p>
            <div className="text-sm space-y-1 max-w-xs">
              <div className="flex justify-between text-slate-600"><span>Revenue (before tax)</span><span>{money(estimates.revenueExTaxCents)}</span></div>
              <div className="flex justify-between text-slate-600"><span>Est. food cost</span><span>−{money(estimates.foodCostCents)}</span></div>
              <div className="flex justify-between text-slate-600"><span>Est. labor ({estimates.laborHours}h)</span><span>−{money(estimates.laborCents)}</span></div>
              <div className="flex justify-between font-semibold border-t border-slate-200 pt-1">
                <span>Est. margin</span>
                <span>{money(estimates.marginCents)}{estimates.marginPct !== null ? ` (${estimates.marginPct}%)` : ''}</span>
              </div>
              <p className="text-xs text-slate-400 pt-1">Estimates only — recipe costs where available, scheduled hours × rates.</p>
            </div>
          </div>
        )}

        {/* Terms */}
        {terms && (
          <div className="py-4">
            <p className="text-xs uppercase tracking-wide text-slate-400 mb-2">Terms</p>
            <p className="text-xs text-slate-600 whitespace-pre-line">{terms}</p>
          </div>
        )}

        <p className="pt-4 text-xs text-slate-400 text-center">
          {isClientView
            ? `We'd love to host you — reply to your proposal email with any questions.`
            : 'Internal document — not for the client.'}
        </p>
      </div>
    </div>
  );
}
